import type { Article } from "../types";

export const salesArticles: Article[] = [
  /* ------------------------------------------------------------------ */
  {
    id: "sales-start-your-day",
    title: "How to start your day on the Today page",
    category: "sales",
    summary: "A five-minute routine that gets you calling the right people first.",
    tags: ["today", "daily", "routine", "home", "dashboard", "morning", "leads"],
    level: "start",
    body: [
      { type: "p", text: "The Today page is built so you can decide what to do in under a minute. Follow the page from top to bottom." },
      {
        type: "steps",
        items: [
          { title: "Open Today", body: "Click Today in the sidebar.", to: "/today", toLabel: "Open Today" },
          { title: "Call any fresh leads", body: "If a box called **Fresh leads — dial now** is at the top, click the first row to open the prospect and call them. The small pill on the left shows how old the lead is." },
          { title: "Read The one thing", body: "The big line under the greeting is what Flowio thinks matters most right now. Click the button beside it (for example **Confirm now** or **Open cockpit**)." },
          { title: "Glance at your numbers", body: "Check MRR, Show rate, Close rate and Sets today. Click a card to open the page behind it." },
          { title: "Prepare for today's calls", body: "Under **Calls today**, click a call to open the prospect and see their details and research." },
        ],
      },
      { type: "callout", tone: "tip", text: "The fresh leads box only appears when there are leads from the last 24 hours that nobody has worked. If it is missing, nothing is waiting." },
    ],
    related: ["sales-today-reference", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-today-reference",
    title: "Today page reference",
    category: "sales",
    summary: "What every section and number on the Today page means and where it comes from.",
    tags: ["today", "mrr", "show rate", "close rate", "sets", "quick actions", "one thing", "reference"],
    level: "basic",
    body: [
      { type: "h", text: "Fresh leads — dial now" },
      { type: "p", text: "Up to 25 leads that arrived in the last 24 hours and still have the status **lead**. Older leads are not shown here; find them on the Pipeline board. Each row shows the lead's name, phone and city, and a copy button for the number." },
      {
        type: "table",
        head: ["Age pill colour", "Meaning"],
        rows: [
          ["Green", "Under 5 minutes old. The best window to call."],
          ["Orange", "Under 1 hour old. Still worth prioritising."],
          ["Grey", "Older than 1 hour."],
        ],
      },
      { type: "h", text: "The one thing" },
      { type: "p", text: "Flowio picks one action for you, in this order:" },
      {
        type: "list",
        items: [
          "If any of today's calls are unconfirmed, it asks you to confirm them and links to the Booking Board.",
          "Otherwise, if calls are booked today, it asks you to prep and links to the Live Call cockpit.",
          "Otherwise it shows a fallback message (your admin can change it) and links to the Drill Range.",
        ],
      },
      { type: "h", text: "The four number cards" },
      {
        type: "table",
        head: ["Card", "What it means"],
        rows: [
          ["MRR", "Monthly recurring revenue: the sum of your active subscriptions. If a goal is set, the card shows progress to it and how many accounts are still needed. The goal is set in Settings, Content."],
          ["Show rate", "The share of booked calls that showed up, over the last 30 days. It comes from outcomes recorded on the Booking Board."],
          ["Close rate", "Deals closed divided by opportunities, over the last 30 days. It comes from calls logged in Call Logger and Live Call."],
          ["Sets today", "Calls booked today from your setter activity, with the dials and leads for today underneath. It is worked out from synced GoHighLevel activity."],
        ],
      },
      { type: "p", text: "If your admin has set targets for show rate and close rate, the small line under each number compares you to them." },
      { type: "h", text: "Quick actions" },
      { type: "p", text: "Shortcuts. Unconfirmed bookings and today's call count appear automatically. Other shortcuts are chosen by your admin in Settings." },
      { type: "h", text: "Calls today" },
      { type: "p", text: "Every call booked for today with the time and source. **confirmed** (green) means the booking has been confirmed; **confirm** (amber) means it still needs confirming. Click a call to open the prospect panel." },
      { type: "h", text: "Follow-ups due" },
      { type: "p", text: "Every account whose most recent logged call is still outcome Follow-up — set the date in Call Logger, or later from the account page's Meetings card. **Overdue** (red) means the date has passed, **Due today** (orange), **Due in Nd** (amber) and **No date set** (grey) if none was given. Logging a newer call on the account (any outcome) removes it from this list. Click a row to open the account." },
    ],
    related: ["sales-start-your-day", "sales-add-a-prospect", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-add-a-prospect",
    title: "How to add a prospect and open their record",
    category: "sales",
    summary: "Three ways to add a new prospect, plus what you can do from the prospect panel.",
    tags: ["new prospect", "add lead", "google maps", "report link", "audit", "account", "create", "prospect drawer"],
    level: "start",
    body: [
      { type: "p", text: "A prospect is a business you hope to sell to. In Flowio every prospect and customer is an **account**." },
      {
        type: "steps",
        items: [
          { title: "Open the Pipeline", body: "Click Pipeline in the sidebar.", to: "/pipeline", toLabel: "Open Pipeline" },
          { title: "Click New prospect", body: "The button is at the top right. A panel opens." },
          { title: "Fastest: paste a Google Maps link", body: "Paste a Maps share link under **Add from Google Maps** and click **Add**. Flowio looks up the business and creates the account from Google's details." },
          { title: "Or paste a report link", body: "If you have a GoHighLevel prospecting report link, paste it under **Add from report link** and click **Add**. Flowio pulls the audit and builds the account from it." },
          { title: "Or fill the form", body: "Type the **Account name** (the only required field), then the owner, phone, email, website, city and state. Choose a **Prospect type**, **Lead source** and **Status**. Enter **Rev/mo $** and **Ticket $** if you know them." },
          { title: "Click Add prospect", body: "A message tells you whether the prospect was also created in GoHighLevel. Then the new account opens." },
        ],
      },
      { type: "callout", tone: "note", text: "Flowio tries to copy every new prospect into GoHighLevel. If it cannot (for example there is no phone or email yet), the message says so and you can push it later from the account page." },
      { type: "h", text: "The prospect panel" },
      { type: "p", text: "On the Today page, clicking a lead or a booked call opens a side panel for that prospect:" },
      {
        type: "list",
        items: [
          "**Live Call** opens the call cockpit for this prospect.",
          "**Open account** opens the full account page.",
          "**AI Research** builds a briefing from their website, Google profile, reviews and map rank. It can take up to a minute. It is AI-generated, so check it.",
          "**Edit** lets you change their details and notes. **Log call** opens Call Logger. **Delete** removes the account for good, after a warning that lists any bookings, calls or subscriptions that go with it.",
        ],
      },
    ],
    related: ["sales-pipeline-reference", "sales-run-a-live-call", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-pipeline-reference",
    title: "Pipeline page reference",
    category: "sales",
    summary: "How the board is laid out, what the columns are and what each card shows.",
    tags: ["pipeline", "board", "stages", "kanban", "gohighlevel", "ghl", "columns", "status", "reference"],
    level: "basic",
    body: [
      { type: "p", text: "The Pipeline is a board with one column per stage. Each card is an account. Click a card to open its overview page." },
      { type: "h", text: "Which columns you see" },
      { type: "p", text: "There are two layouts." },
      {
        type: "table",
        head: ["Layout", "When it is used", "Columns"],
        rows: [
          ["Mirroring GoHighLevel", "Your agency has exactly one pipeline switched on for the board.", "One column per stage of that GoHighLevel pipeline, plus Unsorted if any accounts do not fit a stage. Lost, disqualified and churned accounts are hidden."],
          ["Standard funnel", "Any other time.", "Leads, Booked, Showed, Customers, Closed lost / DQ."],
        ],
      },
      { type: "callout", tone: "note", text: "When mirroring GoHighLevel, move a card by moving it in GoHighLevel and then syncing. You cannot move cards on this board." },
      { type: "h", text: "Standard funnel stages" },
      {
        type: "table",
        head: ["Column", "Account status values"],
        rows: [
          ["Leads", "lead"],
          ["Booked", "booked (a sales call is scheduled)"],
          ["Showed", "showed (they attended the call)"],
          ["Customers", "won or customer"],
          ["Closed lost / DQ", "lost, disqualified (not a fit) or churned (a customer who cancelled)"],
        ],
      },
      { type: "h", text: "What a card shows" },
      {
        type: "list",
        items: [
          "The initials, name, and city and state.",
          "A badge with the prospect's monthly revenue, if known.",
          "The business type and Google review count, if known.",
          "The first two lines of their notes.",
        ],
      },
      { type: "p", text: "Under a column, a line such as **$1,200/mo booked** adds up the real subscription revenue of the accounts in it. It only appears when the total is above zero." },
      { type: "h", text: "New prospect fields" },
      {
        type: "table",
        head: ["Field", "Notes"],
        rows: [
          ["Account name", "Required."],
          ["Prospect type", "A list of business types. Choose Other to type your own."],
          ["Lead source", "How they found you."],
          ["Status", "lead, booked, showed, won, customer or lost. Defaults to lead."],
          ["Rev/mo $ and Ticket $", "Whole dollars. Stored and used in the Revenue Calculator."],
        ],
      },
    ],
    related: ["sales-add-a-prospect", "sales-today-reference", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-run-a-live-call",
    title: "How to run a sales call with the Live Call cockpit",
    category: "sales",
    summary: "A step-by-step routine for using the cockpit before, during and after a call.",
    tags: ["live call", "cockpit", "closer", "call sheet", "timer", "discovery", "script", "objection"],
    level: "start",
    body: [
      {
        type: "steps",
        items: [
          { title: "Choose the prospect", body: "Pick the account in the header, or open the prospect panel and click **Live Call**. Prospect, shop and date are filled in.", to: "/live-call", toLabel: "Open Live Call" },
          { title: "Fill in whatever they're talking about", body: "Every topic shows at once as an open card, from **Before I dial** (which starts with a check of their Google profile: reviews, photos, hours and replies) to **Investment and close** (or **No close yet**), with just its tick boxes and answer boxes. Calls don't go in order: go to the card for whatever the prospect raises and type or tick right there. Tap a card's title for its script (what to say and reminders): on a laptop it opens beside the grid, on a phone it slides up from the bottom." },
          { title: "Say it, then tick it", body: "**Say** lines are what to say; the box under a question is where their answer goes. Tick a check when it's done. Cards colour themselves: grey is not yet, amber is started, green is done. A topic goes green when everything in it is ticked and filled, or when you press **Mark covered** in its script." },
          { title: "Get their numbers", body: "In **The phones** and **The numbers**, type what they tell you: missed calls a week, capacity, booked now, average job, callers who book and bookings that become paid jobs. The recoverable revenue shows once all six are in; until then a list shows what's still to ask. These numbers also update the Revenue Calculator and the Google Maps forecast." },
          { title: "Handle pushback", body: "Tap **Objections** in the header and pick what they said, or **Not a fit?**." },
          { title: "Ask for the sale", body: "Press **+** next to **Asked** in the header each time you ask. Aim for at least three." },
          { title: "Wrap up", body: "Click **Copy summary** and paste it into Slack or the GHL contact. **New call** clears the card after you confirm." },
        ],
      },
      { type: "callout", tone: "tip", text: "Everything on the card is saved to the account as you go, so it's the same on your phone and laptop and still there if you leave the page. With no account picked, it's saved in this browser only." },
    ],
    related: ["sales-live-call-reference", "sales-show-missed-call-math", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-live-call-reference",
    title: "Live Call reference",
    category: "sales",
    summary: "Every part of the Live Call card explained: the header, the topic cards and their colours, the script panel, the numbers box and the info cards.",
    tags: ["live call", "call card", "stages", "checks", "roi", "recoverable revenue", "objections", "copy summary", "asked for the sale", "reference"],
    level: "basic",
    body: [
      { type: "h", text: "Header" },
      { type: "p", text: "Stays at the top. The timer starts and pauses with the play button or the Space bar. The bar counts topics done (for example 5/14). **Asked** counts the times you asked for the sale: press + each time, and aim for three. **Objections** opens the responses to common pushback, **Copy summary** copies the whole card as plain text, and **New call** clears the card after you confirm." },
      { type: "h", text: "Topic cards" },
      { type: "p", text: "One card per topic, all open and in view at once, in call order but meant to be filled in any order. Each shows only its tick boxes and answer boxes, and every answer box is labelled with the question that fills it, in bold. Each part of the call has its own colour, shown as a bar across the top of its cards and under the title: Prep grey, Intro sky blue, Discovery purple, Transition teal, Pitch pink, Close blue. The card itself is grey-tinted until you start it, amber once started and green when done, and the count (for example 3/7) shows how much is filled. In **The numbers**, a line shows the recoverable revenue once all six numbers are in, or which ones it still needs (tap one to jump to its box)." },
      { type: "h", text: "The script" },
      { type: "p", text: "Tap a card's title for its script: the lines to say, the reminders and the full numbers box. On a laptop it sits beside the grid (the X closes it); on a phone it slides up from the bottom. **Mark covered** turns a topic green when you've dealt with it without filling every line. **Back** and **Next** step through the topics. In the intro there is a **Get them on video** check: if they joined by phone, ask if they are near a computer and send the video link again." },
      {
        type: "table",
        head: ["Line", "What it is"],
        rows: [
          ["Remember", "A short reminder in amber: the thing that went wrong on past calls."],
          ["Say", "The line to say, word for word. When it's for a situation, it starts with that, for example \"If they hesitate\"."],
          ["Check", "Something to do. Tick it when it's done. When a check has a ⌄ beside it, tap it for examples of how to do it."],
          ["Box", "Where their answer goes, right under the question that gets it."],
        ],
      },
      { type: "h", text: "Read it back (in Transition)" },
      { type: "p", text: "The Transition card writes the summary for you from what you typed in discovery: the main challenge, the work they want, who answers the phone, their numbers as plain sentences, what they've tried, their goals and why now. Read it to them word for word, then ask \"Have I got that right?\" Only filled-in answers show." },
      { type: "h", text: "Pitch and investment" },
      { type: "p", text: "The pitch card opens with the preface (the outcome, then your proof: his own numbers, the demo line and the trial, never invented case studies), has a check to have him call your demo line (the number from Settings, Content), and wraps up with his recoverable figure and a confidence statement. Investment and close runs in Constantine's order: future-paced next steps, the value stack (The Estimate, The First Ring, The Pin), his number, the price, his number again, then the guarantee." },
      { type: "h", text: "Recoverable revenue (in The numbers)" },
      { type: "p", text: "Missed calls a week × 4.33 × callers who book × bookings that become paid jobs, held to his open slots (capacity minus booked now), × his average job. It uses only numbers he gives you; until all six are in, it lists what's still to ask. It warns when capacity caps the number or when he's full." },
      { type: "h", text: "Info cards" },
      {
        type: "list",
        items: [
          "**Call details**: prospect, shop, date and the Fathom link.",
          "**Call brief**: everything the account page knows for the call (phone, address, Google rating, reviews, reply habits, hours, map rank, the research angle and competitors). Click **Call brief** in the header, or **Open** on the card, to pop it out in a small window that stays on top of your video call (in Chrome and Edge; in other browsers it floats over the page and you can drag it by its title). On a phone it slides up from the bottom.",
          "**Google Maps**: extra customers a month from his Google Maps forecast, updated with the numbers you capture. Money shows once his average job is in.",
        ],
      },
    ],
    related: ["sales-run-a-live-call", "sales-show-maps-forecast", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-show-missed-call-math",
    title: "How to show a prospect their missed-call math",
    category: "sales",
    summary: "Use the calculator on a screen share to turn missed calls into a dollar figure.",
    tags: ["missed call", "calculator", "roi", "presentation", "screen share", "pop out", "recovered revenue"],
    level: "start",
    body: [
      { type: "p", text: "Many local businesses lose money every day because they miss calls. This page turns that into a number the owner can feel." },
      {
        type: "steps",
        items: [
          { title: "Open the Revenue Calculator", body: "Pick the account in the header first if you want the shop name, city and average job filled in.", to: "/missed-call-math", toLabel: "Open the Revenue Calculator" },
          { title: "Ask, then enter their numbers", body: "Set **Calls missed / day** and **Days open / week**, then type their **Average job**." },
          { title: "Set the two rates", body: "Move the **Callers who book** and **Bookings that become paid jobs** sliders. They start between 50 and 100 percent. Click the flag button beside a slider if you need to go lower." },
          { title: "Pick how many months to total", body: "Use **Show totals over** to choose 1 to 24 months. The headline figure covers that many months." },
          { title: "Share it", body: "Click **Presentation mode** for a clean full-screen view of the results. Click **Exit presentation** to come back. Or click **Pop out** to open the calculator in its own window that you can share on a video call." },
        ],
      },
      { type: "callout", tone: "tip", text: "Let the prospect say the numbers themselves. A figure they gave you is far more convincing than one you guessed." },
      { type: "callout", tone: "note", text: "The Live Call cockpit uses the same maths, so numbers you capture there appear here and the other way round." },
    ],
    related: ["sales-missed-call-math-reference", "sales-show-maps-forecast", "sales-run-a-live-call", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-show-maps-forecast",
    title: "How to show a prospect their Google Maps forecast",
    category: "sales",
    summary: "Answer “if I pay you, how many more customers, and how soon?” with his own map ranks and real Google search numbers.",
    tags: ["forecast", "google maps", "map pack", "top 3", "customers", "how soon", "map scan", "reviews", "screen share"],
    level: "start",
    body: [
      { type: "p", text: "The Google Maps forecast is the Revenue Calculator's second tab. It works out how many extra customers a month he gets once he's in Google's top 3 across more of his area, and roughly when." },
      {
        type: "steps",
        items: [
          { title: "Pick the account", body: "Choose the prospect in the header. New leads already have a map scan; if not, click **Scan now** on the account page first (the map scan guide explains what to do if it fails).", to: "/missed-call-math?tab=forecast", toLabel: "Open the forecast" },
          { title: "Check the searches", body: "It starts with the searches for his trade and the areas his map scan covers, and pulls the monthly numbers from Google. Edit the list to match what he does, then tap **Get search numbers**." },
          { title: "Ask his numbers", body: "Average job, callers who book and bookings that become paid jobs. Each one says **From this call** or **Default: ask him**, so you know what's still a guess." },
          { title: "Set the target", body: "How much of the area he reaches the top 3 in, and at which spot. It starts at 60%, what your contract guarantees. Points where he's already in the top 3 keep the spot he holds (a #1 stays a #1 when you pick #3), so a shop that already ranks only gains from the new points. By default the months follow how long he needs to out-review the shop he'd take the spot from." },
          { title: "Walk him through it", body: "Tap the headline or any number to show the working. **Presentation mode** gives a clean full-screen view." },
        ],
      },
      { type: "callout", tone: "tip", text: "The headline is extra customers on top of what he already gets from Google Maps today, so it's a fair “more” number." },
      { type: "callout", tone: "warn", text: "For a body shop, a map scan that spans only a few miles (5×5 at 1 mile apart covers 4 miles across) counts a slice of his market, because collision customers drive further. The forecast says so; run a wider scan (5×5 at 2 miles apart) on the account for a truer number." },
      { type: "callout", tone: "note", text: "Search numbers are Google Ads Keyword Planner averages for the last 12 months. Google counts searches by city or neighborhood, not ZIP code, so the areas are the neighborhoods his map grid covers." },
    ],
    related: ["delivery-map-scan", "sales-show-missed-call-math", "sales-run-a-live-call", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-missed-call-math-reference",
    title: "Revenue Calculator (Missed calls) reference: every number and formula",
    category: "sales",
    summary: "How each input and each result on the calculator is worked out.",
    tags: ["missed call", "formula", "roi", "break-even", "payback", "funnel", "calculation", "reference", "recovered"],
    level: "advanced",
    body: [
      { type: "h", text: "Inputs" },
      {
        type: "table",
        head: ["Input", "Range", "Meaning"],
        rows: [
          ["Calls missed / day", "0 or more", "How many customer calls the business misses on an average day."],
          ["Days open / week", "4 to 7, in half steps", "How many days a week the business is open."],
          ["Callers who book", "50 to 100 percent (1 to 100 with the flag)", "Of the missed callers, the share who would book an appointment."],
          ["Bookings that become paid jobs", "50 to 100 percent (1 to 100 with the flag)", "Of the appointments, the share that become a paid job."],
          ["Show totals over", "1 to 24 months", "How many months the headline figure covers."],
          ["Average job", "Dollars", "The value of one of his jobs."],
        ],
      },
      { type: "h", text: "How the result is calculated" },
      {
        type: "steps",
        items: [
          { title: "Missed inquiries per month", body: "Calls missed / day × Days open / week × 4.33 weeks per month." },
          { title: "Appointments", body: "Missed inquiries × the Callers who book percentage." },
          { title: "Paid jobs", body: "Appointments × the Bookings that become paid jobs percentage." },
          { title: "Recovered revenue per month", body: "Jobs won × Average job." },
          { title: "Headline number", body: "Recovered revenue per month × the projection months. With a 1 month window, the headline shows the monthly figure instead." },
        ],
      },
      { type: "h", text: "The cost-based cards" },
      { type: "p", text: "These use your agency's own monthly price from Settings. **Program cost** is that price multiplied by the projection months. If no price is set, these cards show a dash and prompt you to set one." },
      {
        type: "table",
        head: ["Card", "Formula", "In plain words"],
        rows: [
          ["Jobs to cover N months", "Program cost ÷ Average job", "How many of the prospect's jobs it takes to pay for the program over the months shown."],
          ["ROI", "Recovered revenue over the window ÷ Program cost", "How many times over the program pays for itself, for example 4.0×."],
          ["Payback", "Program cost ÷ (Recovered revenue per month ÷ 30)", "Calendar days of the recovered revenue needed to pay the program cost. These are calendar days, not working days."],
          ["Missed / mo", "Calls missed / day × Days open / week × 4.33 weeks", "Shown with its working, for example 5/day × 6 days × 4.33 weeks."],
        ],
      },
      { type: "h", text: "The funnel" },
      { type: "p", text: "The bar chart shows the same steps: Missed inquiries, Appointments, Jobs won, and Recovered $ / mo, all per month." },
      { type: "callout", tone: "note", text: "The Live Call cockpit's Recovery — live card also shows a 90 day figure. That is simply the monthly recovered revenue multiplied by 3." },
    ],
    related: ["sales-show-missed-call-math", "sales-live-call-reference", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-host-a-video",
    title: "How to host a sales video and embed it on a landing page",
    category: "sales",
    summary: "Upload or generate a video, then copy a link or embed code for your page.",
    tags: ["video", "vsl", "video sales letter", "embed", "upload", "heygen", "landing page", "hosting", "avatar"],
    level: "basic",
    body: [
      { type: "p", text: "A **VSL** (video sales letter) is a video that explains your offer, usually placed on a landing page. This page hosts the file and gives you code to show it anywhere, with no per-view fees." },
      { type: "h", text: "Upload your own file" },
      {
        type: "steps",
        items: [
          { title: "Open VSL Hosting", body: "Click VSL Hosting in the sidebar.", to: "/videos", toLabel: "Open VSL Hosting" },
          { title: "Click Upload video", body: "Choose a video file. MP4 with H.264 video and AAC audio works on every device." },
          { title: "Wait", body: "Large files upload in 32 MB chunks with a progress bar. Keep the tab open until it finishes." },
          { title: "Copy the embed code", body: "When the video shows as live, click **Copy embed code** and paste it into an HTML or embed block in your page builder. It is a responsive 16:9 player. **Copy link** gives a plain link instead, and **Preview** opens the player." },
        ],
      },
      { type: "h", text: "Create a video with HeyGen" },
      { type: "p", text: "HeyGen is an AI video service. You need a HeyGen API key saved in Settings, and each video uses HeyGen credits." },
      {
        type: "steps",
        items: [
          { title: "Click Write a script → get a video", body: "The HeyGen panel opens." },
          { title: "Choose a mode", body: "**From template (branded)** uses a template you built in HeyGen with your brand kit. Pick a template and edit its text fields. **Quick avatar + script** lets you paste a script and pick an avatar, a voice, and a Format (landscape for websites, portrait for ads and reels)." },
          { title: "Give it a title and click Generate video", body: "The video appears in the list as rendering, usually for a few minutes, and goes live by itself when finished." },
        ],
      },
      {
        type: "faq",
        items: [
          { q: "What do the status messages mean?", a: "A ready video shows its size, date and 'live'. 'HeyGen is rendering' means it is still being made. 'failed' shows the reason. 'upload incomplete' means an upload did not finish; upload it again." },
          { q: "What happens if I delete a video?", a: "You are asked to confirm. Every page that embeds it stops showing it, and this cannot be undone." },
          { q: "Can I re-use the embed code later?", a: "Yes. Open Embed code under any ready video to see and copy it again." },
        ],
      },
    ],
    related: ["sales-run-a-live-call", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-build-a-contract",
    title: "How to build a service agreement for a deal",
    category: "sales",
    summary: "Configure the agreement, prefill the client's details and export it as a Word file.",
    tags: ["contract", "agreement", "docx", "word", "guarantee", "prefill", "export", "legal", "sign"],
    level: "basic",
    body: [
      { type: "p", text: "The Contract Builder shows the agreement as a paper document on the right and the settings on the left. Every change updates the document immediately. You need permission to view the contract builder; if it is not in your sidebar, ask an admin." },
      {
        type: "steps",
        items: [
          { title: "Select the account", body: "Pick the client in the header. **Prefill** stays greyed out until you do.", to: "/contract-builder", toLabel: "Open Contract Builder" },
          { title: "Click Prefill", body: "Flowio fills the client's legal details from their Contract details portal form if they submitted it, then fills gaps from the account (name, state, city, email, today's date). Empty fields only." },
          { title: "Choose the Service scope", body: "Use the switches for the lead-gen / CRM service, the guarantee, paid advertising and a website. The document adds or removes whole clauses to match." },
          { title: "Fill in Client & deal", body: "Client legal entity name, state of formation, entity type, DBA, address, agreement date and notice email." },
          { title: "Set Fees & term", body: "Enter the fees and the initial term in months. Fee defaults follow the guarantee switch until you edit a fee yourself." },
          { title: "Check the Provider & governing law section", body: "This is pre-filled with a default company and Nevada law. Change it to your own details if they differ." },
          { title: "Export", body: "Click **Export .docx** to download a Word file, or **.txt** for plain text. The file name includes the client's DBA." },
        ],
      },
      { type: "callout", tone: "warn", text: "If Paid advertising management is on, you must enter a Minimum monthly Ad Spend or the export is blocked. This stops blank placeholders reaching the client." },
      { type: "callout", tone: "note", text: "This tool produces a template document. Have a lawyer review the wording before anyone signs." },
    ],
    related: ["sales-contract-builder-reference", "glossary"],
  },

  /* ------------------------------------------------------------------ */
  {
    id: "sales-contract-builder-reference",
    title: "Contract Builder reference: switches, fields and exports",
    category: "sales",
    summary: "What each switch does, which fields matter, and how the draft is saved.",
    tags: ["contract", "guarantee", "ad spend", "keywords", "radius", "term", "fees", "reference", "toggle"],
    level: "advanced",
    body: [
      { type: "h", text: "Service scope switches" },
      {
        type: "table",
        head: ["Switch", "What it does"],
        rows: [
          ["Lead-gen / CRM service", "The full offer. Turn it off for a website-only deal. That hides every CRM, lead and guarantee clause, turns the guarantee and ads off, and forces the website on."],
          ["Guarantee", "Adds eligibility conditions, the guarantee and refund terms and the worksheet schedule. Shows two extra fields: Revenue target ($) and Ranking position (top N). Off means the standard tier with no guarantee wording. The fee field is labelled Trial fee when the guarantee is on, and Setup fee when off."],
          ["Paid advertising management", "Adds ad spend terms, ad account access and the paid campaign scope. Shows Minimum monthly Ad Spend, which is required to export."],
          ["Website deliverable", "Adds website build and hosting to the scope and fee rows. Shows Website setup fee, Hosting / mo and Pages included. It is locked on when Lead-gen is off."],
        ],
      },
      { type: "h", text: "Sections on the left" },
      {
        type: "table",
        head: ["Section", "Fields"],
        rows: [
          ["Client & deal", "Client legal entity name, State of formation, Entity type, DBA / shop name, Premises / mailing address, Agreement date, Notice email, and (with Lead-gen on) the Call recording notice."],
          ["Fees & term", "Trial fee or Setup fee, Monthly fee, Initial term (months)."],
          ["Local search (Lead-gen only)", "Max primary keywords, the number that must rank in the top 3, the keyword list, Service radius, Min. review rating, Min. resident population, Geo-grid rank tool, Response window and Next-day window (in minutes)."],
          ["Provider & governing law", "Your company's entity name, state, entity type and address, plus Governing law state and Arbitration venue."],
        ],
      },
      { type: "p", text: "Hover the small (i) beside any field for a short explanation. In the recording notice, type [Client DBA] to insert the client's shop name." },
      { type: "h", text: "The paper preview" },
      { type: "p", text: "Values you have typed appear in the document with a dotted underline. Anything still empty shows its placeholder. Numbers are also written out in words where the contract needs it, for example three (3). The four badges above the document show which parts of the service are currently included." },
      { type: "h", text: "Saving and exports" },
      {
        type: "list",
        items: [
          "Your draft (all switches and fields) is saved automatically in this browser. It is not stored on the account and will not appear on another computer.",
          "**Prefill** needs an account. Information from the client's Contract details portal form takes priority over what is already typed; account details only fill blank fields.",
          "**Export .docx** builds a Word document with headings, tables and signature areas. **.txt** exports the visible text only.",
          "Hidden clauses (switched off) are left out of both exports.",
        ],
      },
    ],
    related: ["sales-build-a-contract", "glossary"],
  },
];
